<template>
  <div class="overflow-hidden relative p-4 max-w-screen-xl w-full mx-auto">
    <!-- Loading overlay -->
    <div v-if="isLoading" class="max-h-full absolute inset-0 bg-white bg-opacity-90 z-10">
      <div class="flex items-center max-h-52 justify-center pt-52">
        <Loader2 class="w-4 h-4 mr-2 animate-spin" />
        <span class="text-sm text-gray-600 font-medium">Loading data...</span>
      </div>
    </div>

    <!-- Breadcrumbs -->
    <Breadcrumbs :items="breadcrumbItems" text-color="text-gray-500" />

    <!-- Statistics Overview -->
    <div v-if="statistics && userData" class="mt-4">
      <div class="flex gap-4 md:flex-row flex-col">
        <!-- User Details -->
        <div
          class="flex-shrink-0 w-fit bg-white rounded-lg p-4 md:min-w-80 min-w-full shadow-sm  flex items-center text-center"
        >
          <img
            :src="userData.avatar_image"
            :alt="`${userData.first_name} ${userData.last_name}`"
            class="w-16 h-16 rounded-full object-cover"
          />
          <div class="flex flex-col items-start ml-4">
            <h2 class="text-lg font-medium text-gray-900">
              {{ userData.first_name }} {{ userData.last_name }}
            </h2>
            <div class="text-sm text-gray-500">{{ userData.email }}</div>
          </div>
        </div>

        <!-- User Statistics -->
        <div class="p-4 px-6 rounded-lg bg-white shadow-sm ml-auto">
          <h3 class="text-sm font-medium text-gray-900 mb-4">Review Statistics</h3>
          <div class="gap-8 flex flex-col md:flex-row items-start">
            <!-- Average Rating -->
            <div class="flex items-center gap-2 height-fit min-w-fit h-10">
              <span class="text-yellow-500">★</span>
              <span class="text-2xl font-medium">{{ statistics.average_rating.toFixed(1) }}</span>
              <span class="text-gray-400 text-sm">({{ statistics.total_reviews }} reviews)</span>
            </div>

            <!-- Star Distribution -->
            <div class="flex lg:items-center items-start gap-4 flex-wrap min-h-10">
              <template v-for="rating in [5, 4, 3, 2, 1]" :key="rating">
                <div class="flex items-center gap-1 mr-2">
                  <span class="text-sm text-gray-600">{{ rating }}★</span>
                  <div class="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      class="h-full bg-yellow-500"
                      :style="{
                        width: `${(statistics.star_counts[rating] / statistics.total_reviews) * 100}%`
                      }"
                    ></div>
                  </div>
                  <span class="text-sm text-gray-500">({{ statistics.star_counts[rating] }})</span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Star Filter -->
    <div class="mt-4">
      <Select v-model="selectedStarFilter">
        <SelectTrigger class="w-[180px] bg-white">
          <SelectValue placeholder="Filter by stars" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Stars</SelectItem>
          <SelectItem v-for="star in 5" :key="star" :value="star.toString()">
            {{ star }} Stars
          </SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div class="mt-4 border border-gray-200 rounded-lg overflow-hidden bg-white">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-white">
          <tr>
            <th scope="col" class="w-8 py-3 text-xs font-normal text-gray-500 text-center">#</th>
            <th scope="col" class="w-1/3 py-3 text-left text-xs font-normal text-gray-500 pl-2">
              Task
            </th>
            <th
              scope="col"
              class="w-16 py-3 text-center text-xs font-normal text-gray-500 cursor-pointer"
              @click="sortBy('week_order')"
            >
              Week
              <span v-if="sortKey === 'week_order'" class="ml-1">
                {{ sortOrder === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th
              scope="col"
              class="w-16 py-3 text-center text-xs font-normal text-gray-500 cursor-pointer"
              @click="sortBy('stars_value')"
            >
              Rating
              <span v-if="sortKey === 'stars_value'" class="ml-1">
                {{ sortOrder === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th scope="col" class="w-1/3 py-3 text-left text-xs font-normal text-gray-500 pl-2">
              Review
            </th>
            <th
              scope="col"
              class="w-10 py-3 text-center text-xs font-normal text-gray-500 cursor-pointer"
              @click="sortBy('created_at')"
            >
              Date
              <span v-if="sortKey === 'created_at'" class="ml-1">
                {{ sortOrder === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-if="filteredAndSortedReviews.length === 0">
            <td colspan="6" class="text-center text-gray-500 py-4">No reviews found</td>
          </tr>
          <tr
            v-for="(review, index) in filteredAndSortedReviews"
            :key="review.id"
            class="hover:bg-gray-50"
          >
            <td class="py-4 w-8 text-sm text-gray-400 text-center">{{ index + 1 }}</td>
            <td class="py-4 whitespace-normal text-sm text-gray-900 pl-2">
              <div class="flex flex-col">
                <template v-if="review.path_step?.task">
                  <div class="font-medium">{{ review.path_step.task.name }}</div>
                  <div class="text-xs text-gray-500">
                    {{ review.path_step.path.name }} • {{ review.path_step.task.section.name }}
                  </div>
                </template>
                <template v-else>
                  <div class="text-gray-500">Task information not available</div>
                </template>
              </div>
            </td>
            <td class="py-4 whitespace-normal text-sm text-center">
              {{ review.path_step?.week_order || '-' }}
            </td>
            <td class="py-4 whitespace-normal text-sm text-center">
              <div class="flex items-center justify-center gap-1">
                <span class="text-yellow-500">★</span>
                <span>{{ review.stars_value }}</span>
              </div>
            </td>
            <td class="py-4 whitespace-normal text-sm text-gray-900 pl-2">
              {{ review.review_text || '-' }}
            </td>
            <td class="w-10 py-4 whitespace-normal text-sm text-gray-500 text-center text-pretty">
              {{ formatDate(review.created_at, 'time') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useReviewStore } from '@/stores/review'
import { Loader2 } from 'lucide-vue-next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { formatDate } from '@/utils/formatDate'
import { useExpeditionsStore } from '@/stores/expeditions'
import Breadcrumbs from '@/components/ui/Breadcrumbs.vue'
import type { TaskReview, ReviewStatistics } from '@/types/review'
import type { User } from '@/types/user'

const route = useRoute()
const userId = Number(route.params.userId)
const expeditionsStore = useExpeditionsStore()

const reviewStore = useReviewStore()
const isLoading = ref(false)
const userReviews = ref<TaskReview[]>([])
const statistics = ref<ReviewStatistics | null>(null)
const userData = ref<User | null>(null)

// Sorting and filtering state
const selectedStarFilter = ref('all')
const sortKey = ref<'week_order' | 'stars_value' | 'created_at'>('created_at')
const sortOrder = ref<'asc' | 'desc'>('desc')

// Breadcrumb items
const breadcrumbItems = computed(() => [
  {
    label: 'Expedition Progress',
    to: `/manage/${expeditionsStore.userExpeditionData?.id}`
  },
  {
    label: `${userData.value?.first_name || ''} ${userData.value?.last_name || ''}`.trim() || 'User',
    to: `/manage/${expeditionsStore.userExpeditionData?.id}/${userId}`
  },
  {
    label: 'Review History'
  }
])

// Computed property for filtered and sorted reviews
const filteredAndSortedReviews = computed(() => {
  let filtered = [...userReviews.value]

  // Apply star filter
  if (selectedStarFilter.value !== 'all') {
    filtered = filtered.filter((review) => review.stars_value === Number(selectedStarFilter.value))
  }

  // Apply sorting
  filtered.sort((a, b) => {
    let aValue: any
    let bValue: any

    switch (sortKey.value) {
      case 'week_order':
        aValue = a.path_step?.week_order
        bValue = b.path_step?.week_order
        break
      case 'stars_value':
        aValue = a.stars_value
        bValue = b.stars_value
        break
      case 'created_at':
        aValue = a.created_at
        bValue = b.created_at
        break
    }

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return sortOrder.value === 'asc' ? -1 : 1
    if (bValue === null || bValue === undefined) return sortOrder.value === 'asc' ? 1 : -1

    // Compare values
    if (aValue < bValue) return sortOrder.value === 'asc' ? -1 : 1
    if (aValue > bValue) return sortOrder.value === 'asc' ? 1 : -1
    return 0
  })

  return filtered
})

// Sorting function
const sortBy = (key: 'week_order' | 'stars_value' | 'created_at') => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'desc'
  }
}

onMounted(async () => {
  isLoading.value = true
  try {
    await reviewStore.fetchUserReviews(userId)
    await expeditionsStore.fetchExpeditionByUserId(userId.toString())
    const data = reviewStore.userReviews
    if (data) {
      userData.value = data.user
      userReviews.value = data.reviews
      statistics.value = data.statistics

      // Update document title with fellow's name
      if (data.user.first_name && data.user.last_name) {
        document.title = `${data.user.first_name} ${data.user.last_name} - Review History | Outtalent Campus`
      }
    }
  } finally {
    isLoading.value = false
  }
})
</script>
